import { useState, lazy, Suspense } from 'react';
import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import { LanguageProvider } from '@/lib/LanguageContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import ScrollToTop from './components/ScrollToTop';
import DeepLinkBackStack from './components/DeepLinkBackStack';
import BackHandler from './components/BackHandler';
import ProtectedRoute from '@/components/ProtectedRoute';
import InstantLoader from '@/components/InstantLoader';
import LabelViewerProvider from '@/components/labels/LabelViewerProvider';
import TrackingViewerProvider from '@/components/tracking/TrackingViewerProvider';
import InAppBrowser from '@/components/InAppBrowser';
import SeoHeadInjector from '@/components/seo/SeoHeadInjector';
import AppIconInjector from '@/components/AppIconInjector';
import GlobalTooltipProvider from '@/components/GlobalTooltipProvider';
import TronBackground from '@/components/TronBackground';
import AccessBlockedScreen from '@/components/AccessBlockedScreen';
import SiteBlockedScreen from '@/components/SiteBlockedScreen';
import SwipeNavigation from '@/components/SwipeNavigation';
import GlobalPullToRefresh from '@/components/GlobalPullToRefresh';
import Landing from '@/pages/Landing';
import { appParams } from '@/lib/app-params';
import '@/styles/button-fx.css';

// Layout (sidebar + balance widget + modales de plataforma) solo se usa tras
// login. Cargarlo eager arrastraba ~20 componentes al entry de la landing,
// hundiendo el LCP móvil. Lazy: se carga bajo demanda al entrar a /panel.
const Layout = lazy(() => import('@/components/Layout'));

// Lazy-loaded pages para reducir el bundle inicial y mejorar TTI en móvil/WebView.
const Login = lazy(() => import('@/pages/Login'));
const Register = lazy(() => import('@/pages/Register'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const Home = lazy(() => import('@/pages/Home'));
const CotizacionRapida = lazy(() => import('@/pages/CotizacionRapida'));
const CotizadorPublico = lazy(() => import('@/pages/CotizadorPublico'));
const VendedorStore = lazy(() => import('@/pages/VendedorStore'));
const RastrearPublico = lazy(() => import('@/pages/RastrearPublico'));
const EnvioRuta = lazy(() => import('@/pages/EnvioRuta'));
const EmbedCotizador = lazy(() => import('@/pages/EmbedCotizador'));
const EmbedRastreador = lazy(() => import('@/pages/EmbedRastreador'));
const EmbedSucursales = lazy(() => import('@/pages/EmbedSucursales'));
const CotizadorEmbebible = lazy(() => import('@/pages/CotizadorEmbebible'));
const RastreadorEmbebible = lazy(() => import('@/pages/RastreadorEmbebible'));
const MyShipments = lazy(() => import('@/pages/MyShipments'));
const Pedidos = lazy(() => import('@/pages/Pedidos'));
const ReanudarBorrador = lazy(() => import('@/pages/ReanudarBorrador'));
const CargaMasiva = lazy(() => import('@/pages/CargaMasiva'));
const CargaManual = lazy(() => import('@/pages/CargaManual'));
const Reportes = lazy(() => import('@/pages/Reportes'));
const Analitica = lazy(() => import('@/pages/Analitica'));
const Tracking = lazy(() => import('@/pages/Tracking'));
const WalletPage = lazy(() => import('@/pages/Wallet'));
const Profile = lazy(() => import('@/pages/Profile'));
const Admin = lazy(() => import('@/pages/Admin'));
// Landing se carga eager (import directo arriba) para pintado instantáneo en el primer frame.
const AppSplash = lazy(() => import('@/components/AppSplash'));
const Ayuda = lazy(() => import('@/pages/Ayuda'));
const Privacidad = lazy(() => import('@/pages/Privacidad'));
const Terminos = lazy(() => import('@/pages/Terminos'));
const CalculadoraVolumetrica = lazy(() => import('@/pages/CalculadoraVolumetrica'));
const CarrierPage = lazy(() => import('@/pages/CarrierPage'));
const Recursos = lazy(() => import('@/pages/Recursos'));
const CompararPaqueterias = lazy(() => import('@/pages/CompararPaqueterias'));
const CoberturaCP = lazy(() => import('@/pages/CoberturaCP'));
const Sucursales = lazy(() => import('@/pages/Sucursales'));
const CalculadoraAhorro = lazy(() => import('@/pages/CalculadoraAhorro'));
const EstudioAhorro = lazy(() => import('@/pages/EstudioAhorro'));
const Blog = lazy(() => import('@/pages/Blog'));
const BlogArticle = lazy(() => import('@/pages/BlogArticle'));
const Corporativo = lazy(() => import('@/pages/Corporativo'));
const Agregadores = lazy(() => import('@/pages/Agregadores'));
const ZonasPostales = lazy(() => import('@/pages/ZonasPostales'));
const EnviosInternacionales = lazy(() => import('@/pages/EnviosInternacionales'));
const CotizacionTiempoReal = lazy(() => import('@/pages/CotizacionTiempoReal'));
const RastreoCentralizado = lazy(() => import('@/pages/RastreoCentralizado'));
const RecoleccionAgendable = lazy(() => import('@/pages/RecoleccionAgendable'));
const SaldoPrepago = lazy(() => import('@/pages/SaldoPrepago'));
const MapaLogistico = lazy(() => import('@/pages/MapaLogistico'));
const Soporte = lazy(() => import('@/pages/Soporte'));
const LibretaDirecciones = lazy(() => import('@/pages/LibretaDirecciones'));
const Facturacion = lazy(() => import('@/pages/Facturacion'));
const Notificaciones = lazy(() => import('@/pages/Notificaciones'));
const Integraciones = lazy(() => import('@/pages/Integraciones'));
const PluginWooCommerce = lazy(() => import('@/pages/PluginWooCommerce'));
const IntegracionTiendaNube = lazy(() => import('@/pages/IntegracionTiendaNube'));
const IntegracionShopify = lazy(() => import('@/pages/IntegracionShopify'));
const ApiRestEnvios = lazy(() => import('@/pages/ApiRestEnvios'));
const ApiBHGC = lazy(() => import('@/pages/ApiBHGC'));
const VerificacionIdentidad = lazy(() => import('@/pages/VerificacionIdentidad'));
const Reclamaciones = lazy(() => import('@/pages/Reclamaciones'));
const Recolecciones = lazy(() => import('@/pages/Recolecciones'));
const AsistenteBHGCNexus = lazy(() => import('@/pages/AsistenteBHGCNexus'));
const AsistenteRastreo = lazy(() => import('@/pages/AsistenteRastreo'));
const AsistenteCuenta = lazy(() => import('@/pages/AsistenteCuenta'));
const LabelDownload = lazy(() => import('@/pages/LabelDownload'));
const Comprobante = lazy(() => import('@/pages/Comprobante'));
const TicketViewer = lazy(() => import('@/pages/TicketViewer'));
const Correos = lazy(() => import('@/pages/Correos'));
const Tienda = lazy(() => import('@/pages/Tienda'));
const ProductDetail = lazy(() => import('@/pages/ProductDetail'));
const Carrito = lazy(() => import('@/pages/Carrito'));
const Pagar = lazy(() => import('@/pages/Pagar'));
const AuditoriaValor = lazy(() => import('@/pages/AuditoriaValor'));
const PlanesSaaS = lazy(() => import('@/pages/PlanesSaaS'));
const Franquicia = lazy(() => import('@/pages/Franquicia'));
const TenantAdmin = lazy(() => import('@/pages/TenantAdmin'));
const Autor = lazy(() => import('@/pages/Autor'));
const Directorio = lazy(() => import('@/pages/Directorio'));
const FacebookInsights = lazy(() => import('@/pages/FacebookInsights'));
const Afiliados = lazy(() => import('@/pages/Afiliados'));
const Referidos = lazy(() => import('@/pages/Referidos'));
const IaIntegradas = lazy(() => import('@/pages/IaIntegradas'));
const CotizadorIA = lazy(() => import('@/pages/CotizadorIA'));
const RastreadoresIntegradosApi = lazy(() => import('@/pages/RastreadoresIntegradosApi'));
const ConexionesApiMaps = lazy(() => import('@/pages/ConexionesApiMaps'));
const SeguroAdicionalCudo = lazy(() => import('@/pages/SeguroAdicionalCudo'));
const GuiaConfiguracion = lazy(() => import('@/pages/GuiaConfiguracion'));
const PoliticasEnvio = lazy(() => import('@/pages/PoliticasEnvio'));
const QuienesSomos = lazy(() => import('@/pages/info/QuienesSomos'));
const MisionVisionValores = lazy(() => import('@/pages/info/MisionVisionValores'));
const Cobertura = lazy(() => import('@/pages/info/Cobertura'));
const AliadosLogisticos = lazy(() => import('@/pages/info/AliadosLogisticos'));
const BolsaTrabajo = lazy(() => import('@/pages/info/BolsaTrabajo'));
const PrensaMedios = lazy(() => import('@/pages/info/PrensaMedios'));
const ContactoCorporativo = lazy(() => import('@/pages/info/ContactoCorporativo'));
const About = lazy(() => import('@/pages/About'));
const Contact = lazy(() => import('@/pages/Contact'));
const PoliticaCookies = lazy(() => import('@/pages/legal/PoliticaCookies'));
const TratamientoDatos = lazy(() => import('@/pages/legal/TratamientoDatos'));
const AntiSpam = lazy(() => import('@/pages/legal/AntiSpam'));
const CumplimientoNormativo = lazy(() => import('@/pages/legal/CumplimientoNormativo'));
const PropiedadIntelectual = lazy(() => import('@/pages/legal/PropiedadIntelectual'));
const CodigoEtica = lazy(() => import('@/pages/legal/CodigoEtica'));
const EmpaqueEmbalaje = lazy(() => import('@/pages/envios/EmpaqueEmbalaje'));
const MercanciasFragiles = lazy(() => import('@/pages/envios/MercanciasFragiles'));
const AltoValor = lazy(() => import('@/pages/envios/AltoValor'));
const DocumentosOficiales = lazy(() => import('@/pages/envios/DocumentosOficiales'));
const Perecederos = lazy(() => import('@/pages/envios/Perecederos'));
const BateriasLitio = lazy(() => import('@/pages/envios/BateriasLitio'));
const SegurosEnvio = lazy(() => import('@/pages/envios/SegurosEnvio'));
const LimitacionResponsabilidad = lazy(() => import('@/pages/reclamaciones/LimitacionResponsabilidad'));
const Indemnizacion = lazy(() => import('@/pages/reclamaciones/Indemnizacion'));
const PoliticaReclamaciones = lazy(() => import('@/pages/reclamaciones/Reclamaciones'));
const DanosPerdidas = lazy(() => import('@/pages/reclamaciones/DanosPerdidas'));
const DevolucionesRetornos = lazy(() => import('@/pages/reclamaciones/DevolucionesRetornos'));
const ResolucionControversias = lazy(() => import('@/pages/reclamaciones/ResolucionControversias'));
const ExclusionGarantias = lazy(() => import('@/pages/reclamaciones/ExclusionGarantias'));
const CondicionesUsoPlataforma = lazy(() => import('@/pages/plataforma/CondicionesUsoPlataforma'));
const CondicionesAPI = lazy(() => import('@/pages/plataforma/CondicionesAPI'));
const TerminosIntegradores = lazy(() => import('@/pages/plataforma/TerminosIntegradores'));
const SeguridadInformacion = lazy(() => import('@/pages/plataforma/SeguridadInformacion'));
const Ciberseguridad = lazy(() => import('@/pages/plataforma/Ciberseguridad'));
const SLA = lazy(() => import('@/pages/plataforma/SLA'));
const UsoAceptable = lazy(() => import('@/pages/plataforma/UsoAceptable'));
const RegistroVerificacion = lazy(() => import('@/pages/clientes/RegistroVerificacion'));
const KYC = lazy(() => import('@/pages/clientes/KYC'));
const Antifraude = lazy(() => import('@/pages/clientes/Antifraude'));
const Cancelacion = lazy(() => import('@/pages/clientes/Cancelacion'));
const Reembolsos = lazy(() => import('@/pages/clientes/Reembolsos'));
const MetodosPago = lazy(() => import('@/pages/clientes/MetodosPago'));
const FacturacionCFDI = lazy(() => import('@/pages/clientes/FacturacionCFDI'));
const TerminosProveedores = lazy(() => import('@/pages/proveedores/TerminosProveedores'));
const RequisitosAfiliacion = lazy(() => import('@/pages/proveedores/RequisitosAfiliacion'));
const CalidadServicio = lazy(() => import('@/pages/proveedores/CalidadServicio'));
const ANS = lazy(() => import('@/pages/proveedores/ANS'));
const ResponsabilidadTerceros = lazy(() => import('@/pages/proveedores/ResponsabilidadTerceros'));

// PageLoader (círculo giratorio) reemplazado por InstantLoader (barra fina instantánea).

const RootEntry = () => {
  const { isLoadingAuth, user } = useAuth();
  const [showSplash, setShowSplash] = useState(() => {
    try { return !sessionStorage.getItem("bhgc_splash_seen"); } catch { return true; }
  });
  const hasTrackingParam = (() => { try { return new URLSearchParams(window.location.search).has("rastrear"); } catch { return false; } })();
  // Con token (posible sesión): barra instantánea hasta resolver auth y redirigir.
  // Sin token: el Landing público se pinta de inmediato, sin círculo de carga.
  if (isLoadingAuth && appParams.token && !hasTrackingParam) return <InstantLoader />;
  if (user && !hasTrackingParam) return <Navigate to="/panel" replace />;
  return (
    <>
      <Landing />
      {showSplash && !hasTrackingParam && <AppSplash onClose={() => setShowSplash(false)} />}
    </>
  );
};

const AuthenticatedApp = () => {
  const { authError, navigateToLogin, user } = useAuth();

  // Las rutas públicas (Landing, Tienda, etc.) renderizan de inmediato sin esperar
  // a que resuelva auth. Las rutas protegidas las gating ProtectedRoute (barra instantánea).
  if (authError?.type === 'user_not_registered') {
    return <UserNotRegisteredError />;
  }
  if (authError?.type === 'tenant_blocked') {
    return <SiteBlockedScreen site={authError.site} />;
  }
  if (authError?.type === 'auth_required') {
    navigateToLogin();
    return null;
  }
  // Cuenta suspendida o eliminada por el super admin: el usuario ve un aviso de
  // bloqueo y solo puede cerrar sesión. No accede a la plataforma.
  if (user && (user.status === 'suspendido' || user.status === 'eliminado')) {
    return <AccessBlockedScreen />;
  }

  return (
    <Suspense fallback={<InstantLoader />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/registro" element={<Navigate to="/register" replace />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/" element={<RootEntry />} />
        <Route path="/cotizar" element={<CotizadorPublico />} />
        <Route path="/ayuda" element={<Ayuda />} />
        <Route path="/privacidad" element={<Privacidad />} />
        <Route path="/terminos" element={<Terminos />} />
        <Route path="/politicas-envio" element={<PoliticasEnvio />} />
        <Route path="/info/quienes-somos" element={<QuienesSomos />} />
        <Route path="/info/mision-vision-valores" element={<MisionVisionValores />} />
        <Route path="/info/cobertura" element={<Cobertura />} />
        <Route path="/info/aliados-logisticos" element={<AliadosLogisticos />} />
        <Route path="/info/bolsa-de-trabajo" element={<BolsaTrabajo />} />
        <Route path="/info/prensa" element={<PrensaMedios />} />
        <Route path="/info/contacto-corporativo" element={<ContactoCorporativo />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/legal/cookies" element={<PoliticaCookies />} />
        <Route path="/legal/tratamiento-datos" element={<TratamientoDatos />} />
        <Route path="/legal/anti-spam" element={<AntiSpam />} />
        <Route path="/legal/cumplimiento" element={<CumplimientoNormativo />} />
        <Route path="/legal/propiedad-intelectual" element={<PropiedadIntelectual />} />
        <Route path="/legal/codigo-etica" element={<CodigoEtica />} />
        <Route path="/envios/empaque-embalaje" element={<EmpaqueEmbalaje />} />
        <Route path="/envios/fragiles" element={<MercanciasFragiles />} />
        <Route path="/envios/alto-valor" element={<AltoValor />} />
        <Route path="/envios/documentos-oficiales" element={<DocumentosOficiales />} />
        <Route path="/envios/perecederos" element={<Perecederos />} />
        <Route path="/envios/baterias-litio" element={<BateriasLitio />} />
        <Route path="/envios/seguros" element={<SegurosEnvio />} />
        <Route path="/reclamaciones/limitacion-responsabilidad" element={<LimitacionResponsabilidad />} />
        <Route path="/reclamaciones/indemnizacion" element={<Indemnizacion />} />
        <Route path="/reclamaciones/reclamaciones" element={<PoliticaReclamaciones />} />
        <Route path="/reclamaciones/danos-perdidas" element={<DanosPerdidas />} />
        <Route path="/reclamaciones/devoluciones" element={<DevolucionesRetornos />} />
        <Route path="/reclamaciones/controversias" element={<ResolucionControversias />} />
        <Route path="/reclamaciones/exclusion-garantias" element={<ExclusionGarantias />} />
        <Route path="/plataforma/condiciones-uso" element={<CondicionesUsoPlataforma />} />
        <Route path="/plataforma/condiciones-api" element={<CondicionesAPI />} />
        <Route path="/plataforma/terminos-integradores" element={<TerminosIntegradores />} />
        <Route path="/plataforma/seguridad-informacion" element={<SeguridadInformacion />} />
        <Route path="/plataforma/ciberseguridad" element={<Ciberseguridad />} />
        <Route path="/plataforma/sla" element={<SLA />} />
        <Route path="/plataforma/uso-aceptable" element={<UsoAceptable />} />
        <Route path="/clientes/registro-verificacion" element={<RegistroVerificacion />} />
        <Route path="/clientes/kyc" element={<KYC />} />
        <Route path="/clientes/antifraude" element={<Antifraude />} />
        <Route path="/clientes/cancelacion" element={<Cancelacion />} />
        <Route path="/clientes/reembolsos" element={<Reembolsos />} />
        <Route path="/clientes/metodos-pago" element={<MetodosPago />} />
        <Route path="/clientes/facturacion-cfdi" element={<FacturacionCFDI />} />
        <Route path="/proveedores/terminos" element={<TerminosProveedores />} />
        <Route path="/proveedores/afiliacion" element={<RequisitosAfiliacion />} />
        <Route path="/proveedores/calidad" element={<CalidadServicio />} />
        <Route path="/proveedores/ans" element={<ANS />} />
        <Route path="/proveedores/responsabilidad-terceros" element={<ResponsabilidadTerceros />} />
        <Route path="/calculadora-volumetrica" element={<CalculadoraVolumetrica />} />
        <Route path="/paqueterias/:carrier" element={<CarrierPage />} />
        <Route path="/recursos" element={<Recursos />} />
        <Route path="/comparar-paqueterias" element={<CompararPaqueterias />} />
        <Route path="/cobertura" element={<CoberturaCP />} />
        <Route path="/sucursales" element={<Sucursales />} />
        <Route path="/calculadora-ahorro" element={<CalculadoraAhorro />} />
        <Route path="/estudio-de-ahorro" element={<EstudioAhorro />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogArticle />} />
        <Route path="/corporativo" element={<Corporativo />} />
        <Route path="/agregadores" element={<Agregadores />} />
        <Route path="/zonas-postales" element={<ZonasPostales />} />
        <Route path="/envios-internacionales" element={<EnviosInternacionales />} />
        <Route path="/cotizacion-en-tiempo-real" element={<CotizacionTiempoReal />} />
        <Route path="/rastreo-centralizado" element={<RastreoCentralizado />} />
        <Route path="/recoleccion-agendable" element={<RecoleccionAgendable />} />
        <Route path="/saldo-prepago" element={<SaldoPrepago />} />
        <Route path="/comprobante" element={<Comprobante />} />
        <Route path="/ticket/:type/:id" element={<TicketViewer />} />
        <Route path="/tienda" element={<Tienda />} />
        <Route path="/tienda/:slug" element={<ProductDetail />} />
        <Route path="/vendedor/:slug" element={<VendedorStore />} />
        <Route path="/rastrear" element={<RastrearPublico />} />
        <Route path="/mapa-logistico" element={<MapaLogistico />} />
        <Route path="/envio/:slug" element={<EnvioRuta />} />
        <Route path="/envio" element={<EnvioRuta />} />
        <Route path="/rastrear/:tracking" element={<RastrearPublico />} />
        <Route path="/embed/cotizador" element={<EmbedCotizador />} />
        <Route path="/embed/rastreador" element={<EmbedRastreador />} />
        <Route path="/embed/sucursales" element={<EmbedSucursales />} />
        <Route path="/cotizador-embebible" element={<CotizadorEmbebible />} />
        <Route path="/rastreador-embebible" element={<RastreadorEmbebible />} />
        <Route path="/carrito" element={<Carrito />} />
        <Route path="/pagar" element={<Pagar />} />
        <Route path="/auditoria-valor" element={<AuditoriaValor />} />
        <Route path="/planes-saas" element={<PlanesSaaS />} />
        <Route path="/franquicia" element={<Franquicia />} />
        <Route path="/autor" element={<Autor />} />
        <Route path="/directorio" element={<Directorio />} />
        <Route path="/afiliados" element={<Afiliados />} />
        <Route path="/ia-integradas" element={<IaIntegradas />} />
        <Route path="/cotizador-con-ia" element={<CotizadorIA />} />
        <Route path="/rastreadores-integrados-api" element={<RastreadoresIntegradosApi />} />
        <Route path="/conexiones-api-maps" element={<ConexionesApiMaps />} />
        <Route path="/seguro-adicional-cudo" element={<SeguroAdicionalCudo />} />
        <Route path="/plugin-woocommerce" element={<PluginWooCommerce />} />
        <Route path="/integracion-tiendanube" element={<IntegracionTiendaNube />} />
        <Route path="/integracion-shopify" element={<IntegracionShopify />} />
        <Route path="/api-rest" element={<ApiRestEnvios />} />
        <Route element={<ProtectedRoute unauthenticatedElement={<Navigate to="/login" replace />} />}>
          <Route path="/inicio" element={<Landing />} />
          <Route element={<Layout />}>
            <Route path="/cotizacion-rapida" element={<CotizacionRapida />} />
            <Route path="/panel" element={<Home />} />
            <Route path="/envios" element={<MyShipments />} />
            <Route path="/pedidos" element={<Pedidos />} />
            <Route path="/ordenes" element={<Navigate to="/pedidos" replace />} />
            <Route path="/reanudar-borrador/:id" element={<ReanudarBorrador />} />
            <Route path="/carga-masiva" element={<CargaMasiva />} />
            <Route path="/carga-manual" element={<CargaManual />} />
            <Route path="/reportes" element={<Reportes />} />
            <Route path="/analitica" element={<Analitica />} />
            <Route path="/rastreo" element={<Tracking />} />
            <Route path="/recolecciones" element={<Recolecciones />} />
            <Route path="/asistente" element={<Navigate to="/super-agente" replace />} />
            <Route path="/super-agente" element={<AsistenteBHGCNexus />} />
            <Route path="/asistente-rastreo" element={<AsistenteRastreo />} />
            <Route path="/asistente-cuenta" element={<AsistenteCuenta />} />
            <Route path="/billetera" element={<WalletPage />} />
            <Route path="/perfil" element={<Profile />} />
            <Route path="/soporte" element={<Soporte />} />
            <Route path="/libreta-direcciones" element={<LibretaDirecciones />} />
            <Route path="/facturacion" element={<Facturacion />} />
            <Route path="/referidos" element={<Referidos />} />
            <Route path="/notificaciones" element={<Notificaciones />} />
            <Route path="/integraciones" element={<Integraciones />} />
            <Route path="/api-bhgc" element={<ApiBHGC />} />
            <Route path="/verificacion-identidad" element={<VerificacionIdentidad />} />
            <Route path="/reclamaciones" element={<Reclamaciones />} />
            <Route path="/correos" element={<Correos />} />
            <Route path="/guia-configuracion" element={<GuiaConfiguracion />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/facebook-insights" element={<FacebookInsights />} />
            <Route path="/tenant-admin" element={<TenantAdmin />} />
          </Route>
        </Route>
        <Route path="/:file" element={<LabelDownload />} />
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </Suspense>
  );
};


function App() {
  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <LanguageProvider>
          <Router>
            <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[999] focus:bg-cyan focus:text-ink focus:px-4 focus:py-2 focus:font-bold">
              Saltar al contenido
            </a>
            <TronBackground />
            <ScrollToTop />
            <DeepLinkBackStack />
            <BackHandler />
            <GlobalPullToRefresh>
              <AuthenticatedApp />
            </GlobalPullToRefresh>
            <SwipeNavigation />
            <TrackingViewerProvider />
          </Router>
          <LabelViewerProvider />
          <InAppBrowser />
          <SeoHeadInjector />
          <AppIconInjector />
          <GlobalTooltipProvider />
          <Toaster />
        </LanguageProvider>
      </QueryClientProvider>
    </AuthProvider>
  )
}

export default App